import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { foundationAPI, jobApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';
import { KpiTab, GapTab, ConductTab } from './FoundationStep2';
import { HiringTab, ReviewTab } from './FoundationStep4';
import FoundationDocument from './FoundationDocument';

// The VisionExploit Foundation, step 1 (brainstorms/2026-10-17-foundation-enforcement.md):
// team rules + in-app acknowledgement, the roles register (holders, stand-ins,
// assessors) and the record of signed contracts / confidentiality agreements.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const muted = { color: '#64748b', fontSize: 12 };
const row = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);
const Ok = ({ text }) => (text ? <div style={{ color: '#15803d', fontSize: 13, margin: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}><FiCheckCircle />{text}</div> : null);

const RulesText = ({ body }) => (
  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14, maxHeight: 520, overflowY: 'auto', padding: '4px 2px' }}>{body}</div>
);

// ─── My acknowledgement ────────────────────────────────────────────────────
const MyRules = ({ onChanged }) => {
  const [state, setState] = useState(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(() => foundationAPI.myRules().then((r) => setState(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!state) return <Err text={err} />;
  if (!state.rules) return <div className="cv-subsection-empty">No team rules are in force yet. The Chairman or CEO publishes them.</div>;
  const ack = state.acknowledgement;
  const sign = async () => {
    try { const r = await foundationAPI.acknowledge({ typed_name: name, agree }); setState(r.data); setErr(''); onChanged(); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>{state.rules.title} · v{state.rules.version}</span><span>in force since {fmtDate(state.rules.published_at)}</span></div>
      {state.rules.changes_summary && <div style={{ ...muted, marginBottom: 10 }}>What changed: {state.rules.changes_summary}</div>}
      <RulesText body={state.rules.body} />
      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 12, paddingTop: 12 }}>
        {ack ? (
          <Ok text={`You acknowledged this version on ${fmtDate(ack.acknowledged_at)} as "${ack.typed_name}".`} />
        ) : state.required ? (
          <>
            <Err text={err} />
            {state.overdue && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>Please acknowledge now: the 7 days after publishing have passed.</div>}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 8 }}>
              <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              I have read and understood the VisionExploit team rules and agree to follow them.
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input style={{ flex: 1, minWidth: 220 }} placeholder="Type your full name" value={name} onChange={(e) => setName(e.target.value)} />
              <button className="btn-primary" disabled={!agree || name.trim().split(/\s+/).length < 2} onClick={sign}>Acknowledge</button>
            </div>
            <div style={{ ...muted, marginTop: 6 }}>This replaces the paper signature. The date, time, version and your login are recorded with it.</div>
          </>
        ) : <div style={muted}>Your account does not sign the team rules.</div>}
      </div>
    </div>
  );
};

// ─── Versions, publishing, who has signed ──────────────────────────────────
const RulesAdmin = ({ canManage, stamp, onPublished }) => {
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const load = useCallback(() => foundationAPI.rulesVersions().then((r) => setVersions(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load, stamp]);
  const show = async (v) => {
    try {
      const [full, st] = await Promise.all([foundationAPI.rulesVersion(v.id), v.status === 'DRAFT' ? null : foundationAPI.rulesStatus(v.id)]);
      setOpen(full.data); setStatus(st ? st.data : null); setErr(''); setOk('');
    } catch (e) { setErr(jobApiError(e)); }
  };
  const run = async (fn, done) => { try { await fn(); setErr(''); setOk(done); await load(); } catch (e) { setOk(''); setErr(jobApiError(e)); } };

  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Rules versions</span></div>
      <Err text={err} /><Ok text={ok} />
      {versions.map((v) => (
        <div key={v.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }} onClick={() => show(v)}>
          <strong style={{ minWidth: 60 }}>v{v.version}</strong>
          <span className="status-pill" style={{ color: v.status === 'PUBLISHED' ? '#15803d' : v.status === 'DRAFT' ? '#b45309' : '#64748b' }}>{v.status_label}</span>
          <span style={{ flex: 1, ...muted }}>{v.published_at ? `published ${fmtDate(v.published_at)} by ${v.published_by}` : 'not published'}</span>
        </div>
      ))}
      {canManage && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input style={{ width: 120 }} placeholder="e.g. 1.2" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} />
          <button className="btn-secondary" disabled={!newVersion.trim()}
            onClick={() => run(() => foundationAPI.createRulesVersion({ version: newVersion.trim() }), 'Draft created from the rules in force.').then(() => setNewVersion(''))}>New draft</button>
        </div>
      )}

      {open && (
        <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <div className="cv-subsection-title"><span>v{open.version} · {open.status_label}</span></div>
          {open.status === 'DRAFT' && canManage ? (
            <>
              <label style={muted}>What changed (shown to everyone)</label>
              <textarea rows={2} style={{ width: '100%' }} value={open.changes_summary} onChange={(e) => setOpen({ ...open, changes_summary: e.target.value })} />
              <label style={muted}>Rules text</label>
              <textarea rows={18} style={{ width: '100%', fontSize: 13, lineHeight: 1.5 }} value={open.body} onChange={(e) => setOpen({ ...open, body: e.target.value })} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn-secondary" onClick={() => run(() => foundationAPI.updateRulesVersion(open.id, { body: open.body, changes_summary: open.changes_summary }), 'Draft saved.')}>Save draft</button>
                <button className="btn-primary" onClick={() => window.confirm(`Publish v${open.version}? Everyone will be asked to acknowledge it within 7 days.`)
                  && run(async () => { await foundationAPI.updateRulesVersion(open.id, { body: open.body, changes_summary: open.changes_summary }); await foundationAPI.publishRules(open.id); setOpen(null); onPublished(); }, 'Published. Everyone has been notified.')}>Publish</button>
              </div>
            </>
          ) : <RulesText body={open.body} />}
          {status && (
            <div style={{ marginTop: 12 }}>
              <div className="cv-subsection-title"><span>Acknowledged: {status.done} of {status.total}</span></div>
              {status.people.map((p) => (
                <div key={p.user_id} style={{ ...row, display: 'flex', gap: 8 }}>
                  <span style={{ flex: 1 }}>{p.name} <span style={muted}>· {p.role}</span></span>
                  {p.acknowledged_at
                    ? <span style={{ color: '#15803d' }}>{fmtDate(p.acknowledged_at)} · “{p.typed_name}”</span>
                    : <span style={{ color: '#b45309' }}>Not yet</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Roles register ────────────────────────────────────────────────────────
const RolesTab = ({ people }) => {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState({});
  const [err, setErr] = useState('');
  const load = useCallback(() => foundationAPI.roles().then((r) => setData(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { try { await fn(); setErr(''); setAdding({}); load(); } catch (e) { setErr(jobApiError(e)); } };
  if (!data) return <Err text={err} />;
  const byCode = Object.fromEntries(data.roles.map((r) => [r.code, r]));
  const holderNames = (code) => (byCode[code]?.holders || []).map((h) => h.name).join(', ') || 'no holder yet';
  return (
    <div>
      <Err text={err} />
      <div style={{ ...muted, marginBottom: 8 }}>From the Foundation's RACI chart (Part 2) and "Who assesses whom" (Part 4). Stand-ins are set by role, so they keep working when a holder changes.</div>
      {data.roles.map((r) => (
        <div key={r.id} className="cv-subsection">
          <div className="cv-subsection-title"><span>{r.code} · {r.name}</span>{r.holders_kind === 'MANY' && <span>{r.code === 'CSO' ? 'one per country or city' : 'several people, sharing the work'}</span>}</div>
          <div style={{ fontSize: 13 }}>
            {r.holders.length === 0 && <div style={{ color: '#b45309' }}>No holder yet.</div>}
            {r.holders.map((h) => (
              <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                <strong>{h.name}</strong>{h.area && <span style={muted}>· {h.area}</span>}<span style={muted}>· since {fmtDate(h.started_on)}</span>
                {data.can_manage && <button className="btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => window.confirm(`End ${h.name}'s holding of ${r.code}?`) && run(() => foundationAPI.endHolding(h.id))}>End</button>}
              </div>
            ))}
            <div style={{ ...muted, marginTop: 6 }}>
              Stand-in: <strong>{r.stand_in_note || `${r.stand_in} (${holderNames(r.stand_in)})`}</strong>{r.stand_in_limits && r.stand_in_limits !== 'None' ? ` · while covering: ${r.stand_in_limits}` : ''}
            </div>
            <div style={muted}>Assessed by: {r.assessor ? `${r.assessor} (${holderNames(r.assessor)})` : '—'}{r.assessor_note ? ` · ${r.assessor_note}` : ''}</div>
            {r.past_holders.length > 0 && <div style={muted}>Before: {r.past_holders.map((h) => `${h.name} (to ${fmtDate(h.ended_on)})`).join(', ')}</div>}
          </div>
          {data.can_manage && (r.holders_kind === 'MANY' || r.holders.length === 0) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <select value={adding[r.id]?.user || ''} onChange={(e) => setAdding({ [r.id]: { ...adding[r.id], user: e.target.value } })}>
                <option value="">Add a holder…</option>
                {people.filter((p) => (r.code === 'CSO' ? p.role === 'CSO' : p.role !== 'CSO')).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
              </select>
              {r.holders_kind === 'MANY' && <input placeholder={r.code === 'CSO' ? 'Country or city' : 'Focus (optional), e.g. students'} value={adding[r.id]?.area || ''} onChange={(e) => setAdding({ [r.id]: { ...adding[r.id], area: e.target.value } })} />}
              <button className="btn-primary" disabled={!adding[r.id]?.user} onClick={() => run(() => foundationAPI.addHolder(r.id, { user: parseInt(adding[r.id].user, 10), area: adding[r.id].area || '' }))}>Add</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Agreements ────────────────────────────────────────────────────────────
const AgreementsTab = ({ people, canManage }) => {
  const [records, setRecords] = useState([]);
  const [f, setF] = useState({ user: '', kind: 'CONTRACT', signed_on: '', where_kept: '' });
  const [err, setErr] = useState('');
  const load = useCallback(() => foundationAPI.agreements().then((r) => setRecords(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { try { await fn(); setErr(''); load(); } catch (e) { setErr(jobApiError(e)); } };
  const live = records.filter((a) => !a.withdrawn);
  const csosMissing = people.filter((p) => p.role === 'CSO' && ['CONTRACT', 'CONFIDENTIALITY'].some((k) => !live.some((a) => a.user_id === p.id && a.kind === k)));
  return (
    <div>
      <Err text={err} />
      <div style={{ ...muted, marginBottom: 8 }}>Record that a contract or confidentiality agreement was signed and where the signed copy is kept. The document itself stays outside the system. A Customer Support Officer cannot sign in until both are recorded.</div>
      {csosMissing.length > 0 && (
        <div className="cv-subsection" style={{ borderColor: '#fed7aa' }}>
          <div className="cv-subsection-title"><span>CSOs who cannot sign in yet</span></div>
          {csosMissing.map((p) => <div key={p.id} style={{ fontSize: 13 }}>{p.name}</div>)}
        </div>
      )}
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Records</span></div>
        {records.length === 0 && <div className="cv-subsection-empty">Nothing recorded yet.</div>}
        {records.map((a) => (
          <div key={a.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center', opacity: a.withdrawn ? 0.5 : 1 }}>
            <span style={{ flex: 1 }}><strong>{a.name}</strong> · {a.kind_label} · signed {fmtDate(a.signed_on)} <span style={muted}>· kept: {a.where_kept} · recorded by {a.recorded_by}{a.withdrawn ? ' · withdrawn' : ''}</span></span>
            {canManage && !a.withdrawn && <button className="btn-secondary" onClick={() => window.confirm('Withdraw this record? Use it only for a record entered by mistake.') && run(() => foundationAPI.withdrawAgreement(a.id))}>Withdraw</button>}
          </div>
        ))}
        {canManage && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 1.6fr auto', gap: 6, marginTop: 10 }}>
            <select value={f.user} onChange={(e) => setF({ ...f, user: e.target.value })}>
              <option value="">Person…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </select>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="CONTRACT">Contract</option><option value="CONFIDENTIALITY">Confidentiality agreement</option>
            </select>
            <input type="date" value={f.signed_on} onChange={(e) => setF({ ...f, signed_on: e.target.value })} />
            <input placeholder="Where the signed copy is kept" value={f.where_kept} onChange={(e) => setF({ ...f, where_kept: e.target.value })} />
            <button className="btn-primary" disabled={!f.user || !f.signed_on || !f.where_kept.trim()}
              onClick={() => run(() => foundationAPI.addAgreement({ ...f, user: parseInt(f.user, 10) })).then(() => setF({ user: '', kind: 'CONTRACT', signed_on: '', where_kept: '' }))}>Record</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────
const TeamFoundation = () => {
  const { user } = useAuth();
  const role = user?.role?.toUpperCase();
  const isLeader = ['CHAIRMAN', 'CEO', 'COO'].includes(role) || user?.is_superuser;
  const canManage = ['CHAIRMAN', 'CEO'].includes(role) || user?.is_superuser;
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'rules');
  const [people, setPeople] = useState([]);
  const [stamp, setStamp] = useState(0);
  // Something about the rules changed: reload both panels and the banner.
  const changed = useCallback(() => { setStamp((n) => n + 1); window.dispatchEvent(new Event('vec-rules-changed')); }, []);
  useEffect(() => { foundationAPI.people().then((r) => setPeople(r.data)).catch(() => {}); }, []);
  const tabs = [['foundation', 'Foundation'], ['rules', 'Team rules'], ['roles', 'Roles & stand-ins'], ['kpis', 'KPIs'], ['gap', 'Gap analysis'], ['conduct', 'Conduct'],
    ['hiring', 'Hiring'], ['review', 'Quarterly review'],
    ...(isLeader ? [['agreements', 'Contracts & agreements']] : [])];
  return (
    <div className="students-page">
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => <button key={k} className={tab === k ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === 'rules' && (
        <>
          <MyRules key={stamp} onChanged={changed} />
          {isLeader && <RulesAdmin canManage={canManage} stamp={stamp} onPublished={changed} />}
        </>
      )}
      {tab === 'foundation' && <FoundationDocument isLeader={isLeader} onOpenRules={() => setTab('rules')} />}
      {tab === 'roles' && <RolesTab people={people} />}
      {tab === 'agreements' && isLeader && <AgreementsTab people={people} canManage={canManage} />}
      {tab === 'kpis' && <KpiTab canRefresh={isLeader} />}
      {tab === 'gap' && <GapTab people={people} />}
      {tab === 'conduct' && <ConductTab people={people.filter((p) => p.id !== user?.id)} />}
      {tab === 'hiring' && <HiringTab people={people} myRole={role} />}
      {tab === 'review' && <ReviewTab people={people.filter((p) => p.role !== 'CSO')} />}
    </div>
  );
};

export default TeamFoundation;
